/* My PlatForm transport. All credentials and result proofs stay in memory. */
(function () {
  'use strict';

  function RushError(code, retryAfter) {
    this.name = 'RushError';
    this.code = code;
    this.retryAfter = retryAfter || 0;
  }
  RushError.prototype = Object.create(Error.prototype);

  function normalizeDigits(value) {
    return value.replace(/[٠-٩۰-۹]/g, function (digit) {
      return String(digit.charCodeAt(0) - (digit <= '٩' ? 0x0660 : 0x06f0));
    });
  }

  function Client(origin, enabled) {
    this.enabled = enabled;
    this.csrf = null;
    this.gameToken = null;
    this.origin = '';
    if (origin) {
      try {
        var url = new URL(origin);
        var loopback = ['localhost', '127.0.0.1', '[::1]'].indexOf(url.hostname) !== -1;
        if ((url.protocol !== 'https:' && !(loopback && url.protocol === 'http:')) || url.username || url.password || url.pathname !== '/' || url.search || url.hash) throw new Error();
        this.origin = url.origin;
      } catch (_) { this.enabled = false; }
    }
    this.deviceId = null;
  }

  Client.prototype.request = async function (path, method, body, bearer) {
    if (!this.enabled) throw new RushError('NOT_ENABLED');
    var headers = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (bearer) {
      if (!this.gameToken) throw new RushError('INVALID_GAME_TOKEN');
      headers.Authorization = 'Bearer ' + this.gameToken;
    } else if (method !== 'GET' && this.csrf) headers['X-Finjan-CSRF'] = this.csrf;
    if (path === '/game/grind/sessions') {
      if (!this.deviceId) {
        try { this.deviceId = localStorage.getItem('finjanRush.device.v1'); } catch (_) { /* optional coarse identifier */ }
        if (!/^[a-zA-Z0-9_-]{16,128}$/.test(this.deviceId || '')) {
          this.deviceId = crypto.randomUUID();
          try { localStorage.setItem('finjanRush.device.v1', this.deviceId); } catch (_) { /* memory-only fallback */ }
        }
      }
      headers['X-Finjan-Device-ID'] = this.deviceId;
    }
    var controller = new AbortController();
    var timeout = window.setTimeout(function () { controller.abort(); }, 15000);
    try {
      var response = await fetch(this.origin + '/api/v1' + path, {
        method: method, headers: headers, body: body === undefined ? undefined : JSON.stringify(body),
        credentials: bearer ? 'omit' : 'include', cache: 'no-store', redirect: 'error', signal: controller.signal,
      });
      var data = await response.json().catch(function () { return {}; });
      if (!response.ok) {
        var error = data.detail && data.detail.error;
        throw new RushError(error && typeof error.code === 'string' ? error.code : 'SERVICE_UNAVAILABLE', Number(response.headers.get('Retry-After')) || 0);
      }
      return data;
    } catch (error) {
      if (error instanceof RushError) throw error;
      throw new RushError('NETWORK_ERROR');
    } finally { window.clearTimeout(timeout); }
  };

  Client.prototype.restore = async function () {
    var result = await this.request('/auth/sessions/current', 'GET');
    if (result.status !== 'VERIFIED' || !result.csrfToken) throw new RushError('INVALID_RESPONSE');
    this.csrf = result.csrfToken;
    return result;
  };
  Client.prototype.challenge = async function (phone) {
    var result = await this.request('/auth/otp/challenges', 'POST', {phone: phone, locale: 'en', purpose: 'GRIND_ENTRY'});
    if (result.status !== 'OTP_PENDING' || !result.verificationToken || !Number.isFinite(result.resendAfterSeconds) || !Number.isFinite(result.expiresInSeconds)) throw new RushError('INVALID_RESPONSE');
    return result;
  };
  Client.prototype.verify = async function (token, code) {
    var result = await this.request('/auth/otp/verifications', 'POST', {verificationToken: token, code: code});
    if (result.status !== 'VERIFIED' || !result.csrfToken) throw new RushError('INVALID_RESPONSE');
    this.csrf = result.csrfToken;
    return result;
  };
  Client.prototype.status = function () { return this.request('/game/grind/status', 'GET'); };
  Client.prototype.issue = async function (version, duration) {
    var result = await this.request('/game/grind/sessions', 'POST', {gameVersion: version});
    if (result.status !== 'ISSUED' || !result.gameToken || !result.challengeRef || result.gameVersion !== version || result.durationMs !== duration || !Array.isArray(result.manifest) || !result.manifest.length || result.manifest.length > 128) throw new RushError('INVALID_RESPONSE');
    var previous = -1;
    result.manifest.forEach(function (entry, index) {
      if (entry.spawnId !== index || !Number.isInteger(entry.scheduledElapsedMs) || entry.scheduledElapsedMs < 0 || entry.scheduledElapsedMs < previous || entry.scheduledElapsedMs > duration || ['normal', 'golden', 'burnt', 'bag'].indexOf(entry.itemType) === -1 || !Number.isFinite(entry.xFraction) || entry.xFraction < 0 || entry.xFraction >= 1) throw new RushError('INVALID_RESPONSE');
      previous = entry.scheduledElapsedMs;
    });
    this.gameToken = result.gameToken;
    return result;
  };
  Client.prototype.start = async function () {
    var result = await this.request('/game/grind/sessions/start', 'POST', undefined, true);
    if (result.status !== 'STARTED') throw new RushError('INVALID_RESPONSE');
    return result;
  };
  Client.prototype.submit = async function (proof) {
    var result = await this.request('/game/grind/results', 'POST', proof, true);
    if (result.status !== 'COMPLETED' || result.hasPlayed !== true || !Number.isInteger(result.creditedPoints) || result.creditedPoints < 0) throw new RushError('INVALID_RESPONSE');
    return result;
  };
  window.FinjanRushApi = {Client: Client, normalizeDigits: normalizeDigits};
})();
